import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import Dropdown from "@cayuse/shared-react.components.dropdown";
import TextArea from "@cayuse/shared-react.components.form.inputs.text-area";
import Modal from "@cayuse/shared-react.components.modal";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import RequiredIndicator from "components/RequiredIndicator";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";

import styles from "./styles.css";


export const ActivateModificationModal = ({ activateModalData, isSubaward, canReroute }) => {
	const dictionary = useDictionary("modifications.common.modal.activate");
	const buttonText = useDictionary("modifications.common.buttons");
	const {
		activateDraftModalVisible,
		isLoading,
		formType,
		onActivateDraftClose,
		onCompleteModification,
		reason,
		comment,
		setComment,
		setReason,
	} = activateModalData;

	const { modificationTypes, subawardModificationTypes } = useSelector(tenantSelectors.tenant);
	const [ isRerouting, setIsRerouting ] = useState(false);

	const areActionButtonsDisabled = useMemo(
		() => !isNonEmptyString(reason) || !isNonEmptyString(comment),
		[ reason, comment ]);

	const getModificationTypeOptions = () => {
		const modificationList = isSubaward ? subawardModificationTypes.modifications : modificationTypes.modifications;
		if (modificationList) {
			return modificationList.filter(modification => modification.active)
				.map(modification => ({
					key: modification.value,
					value: modification.value,
					text: modification.value
				}));
		}
	};

	const completeModification = (reroute = false) => {
		setIsRerouting(reroute);
		onCompleteModification(reroute);
	};

	const displayActivateFormContent = () => {
		return (
			<Modal.Content>
				<p>{dictionary.checkReasonText || "Please check that the reason is still correct, and leave a comment."}</p>
				<p className={`${styles.label} ${styles.bold}`}>
					{`${dictionary.label} ${formType}` || `Reason For Modifying ${formType}`}
				</p>
				<div className={styles.modificationTypeWrapper}>
					<Dropdown
						className={styles.modificationType}
						onChange={(event, { value }) => setReason(value)}
						openOnFocus={false}
						options={getModificationTypeOptions()}
						placeholder={dictionary.dropdownPlaceholder}
						search
						value={reason}
					/>
					<div className={`${styles.commentWrapper} ${styles.topMargin}`}>
						<div className={"ui input"}>
							<label>{dictionary.commentLabel || "Comment"}</label>
							{<RequiredIndicator/>}
						</div>
						<TextArea
							onChange={event => setComment(event.target.value)}
						/>
					</div>
				</div>
			</Modal.Content>
		);
	};

	const displayActionButtons = () => {
		return (
			<Modal.Actions>
				<div className="field">
					<Button className={styles.buttonMargin} secondary onClick={onActivateDraftClose}>
						{buttonText.cancel || "Cancel"}
					</Button>
					{isSubaward
						? <Button
							busy={isLoading && !isRerouting}
							onClick={() => completeModification()}
							disabled={areActionButtonsDisabled}
						  >
							{buttonText.continue || "Continue"}
						</Button>
						: <>
							<Button
								className={styles.buttonMargin}
								busy={isLoading && !isRerouting}
								onClick={() => completeModification()}
								disabled={isLoading || areActionButtonsDisabled}
								tooltip={!areActionButtonsDisabled
                                    && ( buttonText.setToActiveTooltip
                                        || "Activates modification and leaves award in current status")}
							>
								{buttonText.setToActive || "Set to Active"}
							</Button>
							{canReroute &&
								<Button
									busy={isLoading && isRerouting}
									onClick={() => completeModification(true)}
									disabled={isLoading || areActionButtonsDisabled}
									tooltip={!areActionButtonsDisabled
										&& ( buttonText.routeForReviewTooltip
											|| "Activates modification, moves award back to the In Review & Negotiation status, and routes the latest award version for review")}
								>
									{buttonText.routeForReview || "Route for Review"}
								</Button>
							}
						</>
					}
				</div>
			</Modal.Actions>
		);
	};

	return (
		<Modal open={activateDraftModalVisible} onClose={onActivateDraftClose}
			ariaLabel={dictionary.title || "Set modification to Active"}
		>
			<Modal.Header className="ui header whiteOnBlue">
				<h2>{dictionary.title || "Set modification to Active"}</h2>
			</Modal.Header>
			{displayActivateFormContent()}
			{displayActionButtons()}
		</Modal>
	);
};


export default ActivateModificationModal;
